import {useAccessPolicy} from './useAccessPolicy';
import AdminLogin, { AdminGate, AdminAccount } from "./pages/AdminLogin";
import { lazy, Suspense, useMemo, useState } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { RunCreateDrawer } from "./components/RunCreateDrawer";
import { useBadCaseSummary, useDatasets, useRuns, useRunSummaries } from "./query";
import type { DashboardDataset, DashboardRun } from "./pages/DashboardPage";
import { audioGenerationStatus, evaluationExecutionComplete } from "./dashboardMetrics";
import { valueOf } from "./types";
import { platformDefinition } from "./platformTaxonomy";

const ReferenceAcceptancePage = lazy(() => import("./pages/ReferenceAcceptancePage"));
const ExternalAudioImportPage = lazy(() => import("./pages/ExternalAudioImportPage"));
const ReferenceLibraryPage = lazy(() => import("./pages/ReferenceLibraryPage"));
const ReferenceAcceptanceReview = lazy(() => import("./pages/ReferenceAcceptanceReview"));
const BlindStudiesPage = lazy(() => import("./pages/BlindStudiesPage"));
const BlindListenPage = lazy(() => import("./pages/BlindListenPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const DatasetVersionsPage = lazy(() => import("./pages/DatasetVersionsPage"));
const RunsPage = lazy(() => import("./pages/RunsPage"));
const RunDetailPage = lazy(() => import("./pages/RunDetailPage"));
const MetricsPage = lazy(() => import("./pages/MetricsPage"));
const ComparisonsPage = lazy(() => import("./pages/ComparisonsPage"));
const AnalysisPage = lazy(() => import("./pages/AnalysisPage"));
const ReviewQueuePage = lazy(() => import("./pages/ReviewQueuePage"));
const BadCasesPage = lazy(() => import("./pages/BadCasesPage"));
const ObservabilityPage = lazy(() => import("./pages/ObservabilityPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ReferenceBoundaryCreatePage = lazy(() => import("./pages/ReferenceBoundaryCreatePage"));

function PageBoundary() {
  return <Suspense fallback={<div className="page-loading">页面加载中…</div>}><Outlet /></Suspense>;
}

function DashboardContainer() {
  const access=useAccessPolicy();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data: runs = [] } = useRuns();
  const {data: generationPage}=useRunSummaries({page:1,pageSize:8});
  const { data: datasets = [] } = useDatasets();
  const { data: badCases } = useBadCaseSummary();
  const dashboardRuns = useMemo<DashboardRun[]>(() => (generationPage?.items??[]).map((run) => {
    const runType = typeof run.run_type === "string"
      ? run.run_type
      : valueOf(run.config, "run_type", valueOf(run.config, "test_type", ""));
    const providerValue = valueOf(run.config, "provider", runType === "reference_boundary" ? "v63_reference_test_run" : "yumemi");
    const provider = platformDefinition(providerValue, runType).label as DashboardRun["provider"];
    const voices = Array.isArray(run.config.voice_ids) ? run.config.voice_ids.map(String) : [];
    const status = audioGenerationStatus(run.summary);
    return {
      id: run.id,
      dataset: valueOf(run.config, "dataset_id"),
      provider,
      voice: voices.length ? `${voices[0]}${voices.length > 1 ? ` 等 ${voices.length} 条` : ""}` : "—",
      model: valueOf(run.config, "model_version"),
      progress: Math.round((run.summary.progress ?? 0) * 100),
      plannedTasks: run.summary.total ?? 0,
      status,
      updatedAt: run.updated_at ? new Date(run.updated_at).toLocaleString("zh-CN") : "—",
    };
  }), [generationPage]);
  const dashboardDatasets = useMemo<DashboardDataset[]>(() => datasets.slice(0, 5).map((dataset, index) => ({
    version: dataset.version_label || dataset.original_name,
    latest: Boolean(dataset.status) && index === 0,
    cases: dataset.summary.enabled_cases,
    language: Object.keys(dataset.summary.languages ?? {}).join("/") || "未提供",
    updatedAt: dataset.created_at ? new Date(dataset.created_at).toLocaleDateString("zh-CN") : "—",
    status: dataset.status === "archived" ? "已归档" : dataset.status ? "已发布" : "本地导入",
  })), [datasets]);
  const completed = runs.filter(evaluationExecutionComplete).length;
  const active = runs.filter((run) => ["running", "queued", "planned", "approved", "analyzing", "stopping"].includes(run.status.toLowerCase())).length;
  return <>
    <DashboardPage
      runs={dashboardRuns}
      datasets={dashboardDatasets}
      reviews={[]}
      metrics={{
        activeRuns: active,
        pendingReviews: null,
        badCases: badCases?.total ?? null,
        badCaseIssues: badCases?.issue_count ?? null,
        passRate: runs.length ? completed / runs.length * 100 : null,
      }}
      onCreateRun={access.can("create_run")?() => setDrawerOpen(true):undefined}
    />
    <RunCreateDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
  </>;
}

export default function App() {
  return <Routes>
    <Route path="blind-listen" element={<Suspense fallback={<p>正在加载…</p>}><BlindListenPage /></Suspense>} />
    <Route path="reference-acceptance/review" element={<Suspense fallback={<p>正在加载验收任务…</p>}><ReferenceAcceptanceReview /></Suspense>} />
    <Route path="login" element={<AdminLogin />} />
    <Route element={<AdminGate />}>
    <Route element={<AppShell />}>
      <Route path="admin-account" element={<AdminAccount />} />
      <Route element={<PageBoundary />}>
        <Route path="speech-rate" element={<Navigate to="/metrics?metric=speech-rate" replace />} />
        <Route path="reference-acceptance" element={<ReferenceAcceptancePage />} />
        <Route path="reference-library" element={<ReferenceLibraryPage />} />
        <Route path="blind-studies" element={<BlindStudiesPage />} />
        <Route index element={<DashboardContainer />} />
        <Route path="datasets" element={<DatasetVersionsPage />} />
        <Route path="runs" element={<RunsPage />} />
        <Route path="runs/new/reference-matrix" element={<Navigate to="/runs/new/reference-boundary" replace />} />
        <Route path="runs/new/reference-boundary" element={<ReferenceBoundaryCreatePage />} />
        <Route path="runs/:runId/audio-import" element={<ExternalAudioImportPage />} />
        <Route path="runs/:runId" element={<RunDetailPage />} />
        <Route path="metrics" element={<MetricsPage />} />
        <Route path="review-tags" element={<Navigate to="/metrics?tab=tags" replace />} />
        <Route path="comparisons" element={<ComparisonsPage />} />
        <Route path="analysis" element={<AnalysisPage />} />
        <Route path="reviews" element={<ReviewQueuePage />} />
        <Route path="bad-cases" element={<BadCasesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/observability" element={<ObservabilityPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Route>
    </Route>
  </Routes>;
}
